/**
 * Toast Context
 * 전역 Toast 상태 관리
 */
import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { ToastMessage, ToastType, ToastPosition, ToastOptions } from './Toast.types';

/**
 * Toast Context 타입
 */
interface ToastContextType {
    toasts: ToastMessage[];
    showToast: (message: string, options?: ToastOptions) => void;
    hideToast: (id: string) => void;
    clearAllToasts: () => void;
}

/**
 * Toast Context
 */
const ToastContext = createContext<ToastContextType | undefined>(undefined);

/**
 * Toast Provider Props
 */
interface ToastProviderProps {
    children: ReactNode;
    /**
     * Toast 기본 지속 시간 (밀리초)
     */
    defaultDuration?: number;
    /**
     * Toast 기본 위치
     */
    defaultPosition?: ToastPosition;
}

/**
 * Toast Provider
 */
export const ToastProvider: React.FC<ToastProviderProps> = ({
    children,
    defaultDuration = 3000,
    defaultPosition = ToastPosition.TOP_CENTER,
}) => {
    const [toasts, setToasts] = useState<ToastMessage[]>([]);

    /**
     * Toast 숨기기
     */
    const hideToast = useCallback((id: string) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    /**
     * Toast 표시
     */
    const showToast = useCallback(
        (message: string, options: ToastOptions = {}) => {
            const timestamp = Date.now();
            const random = Math.random();
            const id = `toast-${timestamp}-${random}`;
            const toast: ToastMessage = {
                id,
                message,
                type: options.type || ToastType.INFO,
                duration: options.duration || defaultDuration,
                position: options.position || defaultPosition,
            };

            setToasts((prev) => [...prev, toast]);

            // 자동 삭제
            if (toast.duration && toast.duration > 0) {
                setTimeout(() => {
                    hideToast(id);
                }, toast.duration);
            }
        },
        [defaultDuration, defaultPosition, hideToast]
    );

    /**
     * 모든 Toast 삭제
     */
    const clearAllToasts = useCallback(() => {
        setToasts([]);
    }, []);

    return (
        <ToastContext.Provider
            value={{
                toasts,
                showToast,
                hideToast,
                clearAllToasts,
            }}
        >
            {children}
        </ToastContext.Provider>
    );
};

/**
 * Toast Hook
 */
export const useToast = (): ToastContextType => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within ToastProvider');
    }
    return context;
};
